<script setup lang="ts">
import {computed, onActivated, onDeactivated, onMounted, onUnmounted, reactive, ref, watch} from "vue";
defineOptions({ name: 'ChatsOpen' })
import EmptyState from "../../components/global/EmptyState.vue";
import Pagination from "../../components/section/Pagination.vue";
import SectionHeader from "../../components/section/SectionHeader.vue";
import ChatRowItem from "../../components/chats/ChatRowItem.vue";
import {useChatStore} from "../../store/chat";
import {useUserStore} from "../../store/users";
import {useModalStore} from "../../store/modal";
import {useAuthStore} from "../../store/auth.js";

const store = useChatStore()
const userStore = useUserStore()
const authStore = useAuthStore()
const roles = ref([])
const modalStore = useModalStore()
const select_all = computed(() => selected_chats.value.length === chats.value.length)
const selected_chats = ref([])
const chats = ref([{id: 1}, {id: 2}])
const search = ref()
const chatParams = reactive({
    sort: 'desc',
    status: null,
    assignee_id: null,
    assignment: null,
    start_date: null,
    end_date: null,
    limit: 25,
    read_status: null
})
const chatSortOptions = ref(
    [
        {
            label: "Latest first",
            value: "desc"
        },
        {
            label: "Oldest first",
            value: "asc"
        },
    ]
)
const chatAssignmentOptions = ref([
    {label: 'All', value: null},
    {label: 'Unassigned', value: 'unassigned'},
    {label: 'Assigned', value: 'assigned'},
])
const chatReadOptions = ref([
    {label: 'All', value: null},
    {label: 'Unread', value: 'unread'},
    {label: 'Read', value: 'read'},
])
const chatStatusOptions = ref([])
const assigneeOptions = ref([])
const scrollSection = ref()
let searchDebounce

const RESPONSE_OVERDUE_MS = 30 * 60 * 1000
const now = ref(Date.now())
let overdueTicker

const isOverdue = (chat) => {
    if (!chat?.awaiting_response_since || chat?.completed_at) {
        return false
    }

    return now.value - new Date(chat.awaiting_response_since).getTime() >= RESPONSE_OVERDUE_MS
}

const getChats = async (page = 1) => {
    await store.getChats({
        ...chatParams,
        page: page,
        completed: 0
    })

    if (store.chats?.data && store.chats?.data?.length) {
        scrollSection.value.scrollTop = 0
    }

    clearSelection()
}

const handleSearch = async (query) => {
    await store.searchChats(query, null)
}
const clearOtherFilters = () => {
    chatParams.sort = 'desc';
    chatParams.assignee_id = null;
    chatParams.assignment = null;
}
const clearDateFilters = () => {
}

const selectChat = (id: number) => {
    const index = selected_chats.value.indexOf(id)

    if (index !== -1) {
        selected_chats.value.splice(index, 1)
    } else {
        selected_chats.value.push(id)
    }
}

const clearSelection = () => {
    selected_chats.value = []
}

const toggleSelects = () => {
    if (select_all.value) {
        selected_chats.value = []
    } else {
        store.chats?.data.forEach(chat => {
            if (!selected_chats.value.includes(chat.id)) {
                selected_chats.value.push(chat.id)
            }
        })
    }
}

watch((search), async (value) => {
    clearTimeout(searchDebounce)
    if (value) {
        searchDebounce = setTimeout(async () => {
            await handleSearch(value)
        }, 250)
    } else {
        await getChats()
    }
})

onMounted(async () => {
    if (authStore._user.roles) {
        authStore._user.roles.forEach(role => {
            roles.value.push(role?.name)
        });

        chatParams.assignee_id = !roles.value.includes('internal-team-leader') ? authStore._user.id : null
    }
    await userStore.getAllUsers({all: false, role: 'internal-agent'});
    userStore.users.forEach(u => {
        assigneeOptions.value.push({label: `${u.first_name} ${u.last_name}`, value: u.id})
    })

    window.Echo.private(`chats`)
        .listen('ChatCreated', async (e) => {
            await getChats()
        })
})

onActivated(async () => {
    now.value = Date.now()
    overdueTicker = setInterval(() => now.value = Date.now(), 30 * 1000)
    await getChats()
})

onDeactivated(() => {
    clearInterval(overdueTicker)
    store.resetChats()
})

onUnmounted(() => {
    window.Echo.leave(`private-chats`)
})
</script>

<template>
    <main v-if="!chats">
        <empty-state icon="speechbubble-sms" heading="Loading chats." content="Give it sec, it's coming in a mo."/>
    </main>

    <main v-else-if="search || (store.chats?.data?.length > 0)">
        <section>
            <section-header :heading="`Open chats.`">
                <div class="search-bar">
                    <brand-form-input label="Search by chat or order number" variant="search" v-model="search"/>
                </div>
                <brand-button variant="small" @click="modalStore.open('ManualCreateChat', {}, 'body', 'bottom-right')">
                    New Chat
                    <ah-icon name="plus-default"/>
                </brand-button>
            </section-header>
            <div class="select-all">
                <label for="select-all-input" class="select-all-label" @click.prevent="toggleSelects">
                    <ah-icon :name="select_all ? `checkbox-selected` : `checkbox-deselected`"/>
                    <input type="checkbox" name="" id="select-all-input" v-model="select_all">
                    {{ select_all ? 'Deselect' : 'Select' }} all.
                </label>

                <div class="select-all-actions" v-if="selected_chats.length > 0">
                    <p>{{ selected_chats.length }} Chat(s) selected.</p>
                    <div class="select-all-actions-buttons">
                        <brand-button variant="small" type="button"
                                      @click="modalStore.open('AssignChat', {id: selected_chats}, 'body', 'bottom-right')">
                            Assign
                            <ah-icon name="user-single-add"/>
                        </brand-button>
                        <brand-button variant="small" type="button"
                                      @click="modalStore.open('CompleteChat', {id: selected_chats}, 'body', 'bottom-right')">
                            Complete
                            <ah-icon name="check-all"/>
                        </brand-button>
                    </div>
                    <a @click.prevent="selected_chats = []">Clear</a>
                </div>
            </div>
            <div class="section-table" ref="scrollSection" v-if="store.chats?.data?.length">
                <chat-row-item v-for="c in store.chats?.data"
                               :id="c.id"
                               :checked="selected_chats.includes(c.id)"
                               :client="c?.contact?.client ?? {first_name: c?.contact?.profile_name, last_name: '', cell_number: c.phone_number}"
                               :unread_message_count="c?.unread_message_count ?? null"
                               :created_at="c.latest_message_date ?? c.created_at"
                               :assignee="c.assignee"
                               :cell_number="c.phone_number"
                               :completed_at="c?.completed_at ?? null"
                               :completed_by_id="c?.completed_by_id ?? null"
                               :bulk_message="c.bulk_message ?? false"
                               :is_overdue="isOverdue(c)"
                               @select-chat="selectChat"
                />
            </div>
            <div class="search-empty-container" v-else-if="search && !store.chats?.data?.length">
                <h1>Sorry, there are no chats containing “{{ search }}”.</h1>
            </div>
            <div class="section-table-empty" v-if="!store.chats?.data?.length">
                <h1>No chats match</h1>
            </div>
            <div class="section-footer" v-if="store.chats?.data?.length">
                <pagination :pagination="store?.chats" @change-page="getChats"/>
            </div>
        </section>
        <aside :class="['section-sidebar', {'section-sidebar-no-search':search && !store.chats?.data?.length}]">
            <div class="sidebar-header">
                <h2>Filters</h2>
                <p @click.prevent="clearOtherFilters">Clear</p>
            </div>
            <div class="sidebar-select">
                <brand-form-select variant="sidebar" label="Sorting." :options="chatSortOptions"
                                   v-model="chatParams.sort"/>
            </div>
            <!--            <div class="sidebar-select">-->
            <!--                <brand-form-select variant="sidebar" label="Status." :options="chatStatusOptions"-->
            <!--                                   v-model="chatParams.status"/>-->
            <!--            </div>-->


            <div class="sidebar-select" v-if="roles && roles.includes('internal-team-leader')">
                <brand-form-select variant="sidebar" label="Assignee." :options="assigneeOptions"
                                   v-model="chatParams.assignee_id"/>
            </div>

            <div class="sidebar-select" v-if="roles && roles.includes('internal-team-leader')">
                <brand-form-select variant="sidebar" label="Assignment." :options="chatAssignmentOptions ?? []"
                                   v-model="chatParams.assignment"/>
            </div>

            <div class="sidebar-select">
                <brand-form-select variant="sidebar" label="Read status." :options="chatReadOptions ?? []"
                                   v-model="chatParams.read_status"/>
            </div>

            <div class="sidebar-header">
                <h2>Date</h2>
                <p @click.prevent="clearDateFilters">Clear</p>
            </div>
            <brand-form-input label="Start date." variant="date" v-model="chatParams.start_date"/>
            <brand-form-input label="End date." variant="date" v-model="chatParams.end_date"/>

            <div class="sidebar-footer">
                <brand-button as="button" variant="primary" @click.prevent="getChats(1)">Apply</brand-button>
                <!--                <brand-button as="button" variant="small">-->
                <!--                    <ah-icon name="arrow-in"/>-->
                <!--                </brand-button>-->
            </div>
        </aside>
    </main>

    <main v-else>
        <empty-state icon="speechbubble-sms" heading="No open chats."
                     content="Once a chat is open it will show up here."/>
    </main>
</template>

<style scoped lang="scss">
.select-all {
    margin: 2rem;
    display: flex;
    justify-content: space-between;

    &-label {
        display: flex;
        align-items: center;
        gap: 1rem;
        @include font(1.4, 1.4, black);
        color: var(--color-thunder);

        input {
            display: none;
        }

        .icon {
            fill: var(--color-hazy);
        }
    }

    &-actions {
        display: flex;
        gap: 2rem;
        align-items: center;

        p {
            @include font(1.4, 1.4, black);
            color: var(--color-thunder);

            @include dark {
                color: var(--color-white);
            }
        }

        &-buttons {
            display: flex;
            gap: .5rem;

            button {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 1rem;
                width: 12rem;
                height: 3.6rem;
                color: var(--color-white) !important;
                background-image: linear-gradient(106deg, #D50032 0%, #D50032 30%, #BA0C2F 70%, #BA0C2F 100%);

                .icon {
                    fill: var(--color-white);
                }
            }
        }

        a {
            @include font(1.1, 1.1, black);
            color: var(--color-tiffany-dark);
            cursor: pointer;
        }
    }

    @include dark() {
        &-label {
            color: var(--color-white);

            .icon {
                fill: var(--color-white);
            }
        }
    }
}

.section-table {
    height: calc(100% - 18rem);
}

.search-empty-container {
    padding-top: 2rem;

    h1 {
        text-align: center;
    }
}
</style>

<style scoped lang="scss">
.search-bar {
    min-width: 34rem;

    @include mobile() {
        min-width: 0;
    }
}
</style>
